import { PayloadAction } from '@reduxjs/toolkit'
import { RequestStatus } from 'features/common'
import { all, call, debounce, put, select, takeEvery } from 'redux-saga/effects'
import {
  addToIgnoredProcessingJobs,
  checkProcessingJob,
  createProcessingJob,
  deleteProcessingJob,
  getProcessingJobsList,
  setIgnoredProcessingJobs
} from '../api/processingJobs'
import {
  ProcessingJob,
  JobStatusType,
  JobType,
  ProcessingJobPayload,
  ResponseStatus,
  JobErrorCode,
  ImportingJobPayload,
  ProcessingJobStatus
} from '../types'
import {
  addJob,
  checkExportingJobStatusRequest,
  createExportingJobRequest,
  getExportingJobsRequest,
  setCreateExportingJobStatus,
  addExportingJobInProgress,
  removeExportingJobInProgress,
  setExportingJobs,
  setExportingJobsStatus,
  updateJob,
  deleteExportingJobRequest,
  removeFromExportingJobs,
  clearExportingJobsRequest,
  removeExportingJobs,
  setCurrentExportingJob,
  addExportJob,
  updateExportJob,
  removeExportJob
} from './jobsExportingSlice'
import { CARTO_IGNORED_PROCESSING_JOBS } from '../types'
import { getLocalStorageItem } from 'app/api/localStorage'
import { updateDataExplorer } from 'features/data-explorer/state/dataExplorerSagas'
import { getMetricsSagasFunction } from 'features/metrics/utils'
import { storeLocalExportTasks } from '../api/exportJobs'
import { pollJobUntilFinished } from './jobsSagas'

function* createExportingJobAndPolling(
  action: PayloadAction<{ connection: string; processingJobPayload: ProcessingJobPayload }>
) {
  try {
    yield put(setCreateExportingJobStatus(RequestStatus.Pending))
    yield call(setExportingJob, action)
  } catch (e) {
    yield put(setCreateExportingJobStatus(RequestStatus.Rejected))
  }
}

function* setExportingJob(
  action: PayloadAction<{ connection: string; processingJobPayload: ProcessingJobPayload }>,
  error?: { status: string; error: string }
) {
  try {
    const data: ProcessingJob = yield call(createProcessingJob, action.payload)
    yield put(addExportingJobInProgress(data.jobId))
    yield put(setCurrentExportingJob(data))
    if (error) {
      data.dryRun = error
      data.status = {
        jobId: data.jobId,
        status: JobStatusType.Failure,
        error: {
          msg: error.error,
          code: error.status
        }
      }
    }
    yield put(addJob(data))
    yield put(checkExportingJobStatusRequest([data]))
    yield put(setCreateExportingJobStatus(RequestStatus.Fulfilled))
    yield put(setCreateExportingJobStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setCreateExportingJobStatus(RequestStatus.Rejected))
  }
}

export function exportingJobFilter(job) {
  const jobs = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
  if (jobs?.length) {
    return !jobs.some((j) => j.jobId === job.jobId)
  }
  return !(job?.status?.status === JobStatusType.Failure && job.status?.error?.code === JobErrorCode.Stopped)
}

function* clearExportingJobs(action: PayloadAction<ProcessingJob[]>) {
  try {
    yield put(setExportingJobsStatus(RequestStatus.Pending))
    yield call(setIgnoredProcessingJobs, { jobs: action.payload })
    yield put(removeExportingJobs(action.payload))
    yield put(setExportingJobsStatus(RequestStatus.Fulfilled))
    yield put(setCurrentExportingJob())
  } catch (e) {
    console.error(e)
    yield put(setExportingJobsStatus(RequestStatus.Rejected))
  }
}

function* getExportingJobs(action: PayloadAction<ImportingJobPayload>) {
  try {
    yield put(setExportingJobsStatus(RequestStatus.Pending))

    const exportingJobs = yield call(getProcessingJobsList, action.payload)
    const filteredExportingJobs: ProcessingJob[] = (exportingJobs || [])
      .filter((job) => job.metadata.type === JobType.Exporting)
      .filter((job) => job.metadata.connection)
      .map((job) => {
        return { jobId: job.externalId, connection: job.metadata.connection, response: job }
      })

    yield put(setExportingJobs(filteredExportingJobs))
    yield put(setExportingJobsStatus(RequestStatus.Fulfilled))

    const jobsToBeChecked = filteredExportingJobs.filter(
      (job) => !job.status || job.status.status === JobStatusType.Pending || job.status.status === JobStatusType.Running
    )
    yield put(checkExportingJobStatusRequest(jobsToBeChecked))
  } catch (e) {
    console.error(e)
  }
}

function* pollingAllExportingJobsStatus(action: PayloadAction<ProcessingJob[]>) {
  const jobs = action.payload
  try {
    yield all(
      jobs.map(
        yield (job) => {
          return call(pollingExportingJobStatus, job)
        }
      )
    )
  } catch (e) {
    console.error(e)
  }
}

function* pollingExportingJobStatus(job: ProcessingJob) {
  try {
    const jobStatus: ProcessingJobStatus = yield pollJobUntilFinished(() =>
      checkProcessingJob({
        connection: job.connection as string,
        jobId: job.jobId
      })
    )
    const currentExportingJob: ProcessingJob | undefined = yield select(
      (state) => state.jobsExporting.currentExportingJob
    )
    const exportingJobs: string[] = yield select((state) => state.jobsExporting.exportingJobsInProgress)

    if (jobStatus?.status === JobStatusType.Success && exportingJobs.includes(job.jobId)) {
      yield put(removeExportingJobInProgress(job.jobId))
      yield call(updateDataExplorer, job)

      if (currentExportingJob) {
        yield put(setCurrentExportingJob({ ...job, status: jobStatus }))
      }
      // if current job finished and its ok
      if (currentExportingJob?.jobId === job.jobId) {
        yield call(sendMetrics, { job, statistics: jobStatus.provider?.statistics, isCompleted: 'yes' })
      }
    }
    if (jobStatus.status === JobStatusType.Failure && jobStatus.error?.code === JobErrorCode.Stopped) {
      yield put(removeFromExportingJobs(job.jobId))
      yield put(setCurrentExportingJob())
    } else {
      // if current job finished with error
      if (jobStatus.status === JobStatusType.Failure && currentExportingJob?.jobId === job.jobId) {
        yield call(sendMetrics, { job, statistics: jobStatus.provider?.statistics, isCompleted: 'no' })
      }
      yield put(updateJob(jobStatus))
      if (currentExportingJob) {
        yield put(setCurrentExportingJob({ ...job, status: jobStatus }))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function* cancelExportingJob(action: PayloadAction<{ job: ProcessingJob }>) {
  try {
    const data: { status: string } = yield call(deleteProcessingJob, {
      jobId: action.payload.job.jobId,
      connection: action.payload.job.connection as string
    })
    const job = action.payload.job
    if (data.status === ResponseStatus.Ok) {
      yield call(addToIgnoredProcessingJobs, job)
      yield put(removeExportingJobInProgress(job.jobId))
      yield put(removeFromExportingJobs(job.jobId))
      yield put(setCurrentExportingJob())
      yield call(sendMetrics, { job, statistics: null, isCompleted: 'cancelled' })
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw Error(e)
  }
}

function* syncExportJobsWithLocalStorage() {
  try {
    const exports = yield select((state) => state.jobsExporting.exports)
    const userId = yield select((state) => state.users.me?.user_id)
    if (!userId) return

    yield call(storeLocalExportTasks, userId, exports)
  } catch (error) {
    console.error('syncTasksWithLocalStorate failed', error)
  }
}

function* watchFetchJobs() {
  yield takeEvery(deleteExportingJobRequest.type, cancelExportingJob)
  yield takeEvery(createExportingJobRequest.type, createExportingJobAndPolling)
  yield takeEvery(getExportingJobsRequest.type, getExportingJobs)
  yield takeEvery(clearExportingJobsRequest.type, clearExportingJobs)

  try {
    yield debounce(500, [addExportJob.type, updateExportJob.type, removeExportJob.type], syncExportJobsWithLocalStorage)
    yield debounce(1000, checkExportingJobStatusRequest.type, pollingAllExportingJobsStatus)
  } catch (e) {
    // TODO
  }
}

function* sendMetrics({ job, statistics, isCompleted }) {
  try {
    if (job.response.metadata.event) {
      const metricsFn = getMetricsSagasFunction(job.response.metadata.event.name)
      if (metricsFn) {
        yield put(metricsFn({ job: job.response.metadata, statistics, isCompleted }))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

export default function* jobsSaga() {
  try {
    yield all([watchFetchJobs()])
  } catch (e) {
    //TODO
  }
}
